/**
 * The shapes you can put on the canvas.
 *
 * Two ways to place one, because both are habits people already have:
 * DRAG it where you want it, or CLICK it and have it land in the middle
 * of the view. The click path matters more than it looks — on a chain of
 * four hundred parties the drop point is usually "wherever there is
 * room", and dragging across a full screen to find that is worse than
 * dropping it in the middle and nudging.
 *
 * Each item draws itself as the shape it makes, at the colours it makes
 * it in. A list of names would be a list of names; the pink square IS
 * the life estate, and that is the whole reason a palette is visual.
 */
import { useMemo, useState } from 'react';
import { SHAPE_LIBRARY, searchShapes } from '../../lib/canvas/shapes.js';
import { shapePath } from './shapePath.js';
import './ShapeLibrary.css';

/** A small drawing of what the item will make. */
function Preview({ item }) {
  const w = 34;
  const h = item.shapeType === 'text' ? 16 : 26;
  const path = shapePath(item.shapeType, w, h);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      {path ? (
        <path
          d={path}
          fill={item.fill && item.fill !== 'transparent' ? item.fill : '#ffffff'}
          stroke={item.stroke === 'transparent' ? 'none' : (item.stroke || '#3a414a')}
          strokeWidth={1.5}
        />
      ) : (
        <text
          x={w / 2} y={h - 4} textAnchor="middle" fontSize="13"
          fill={item.textColor || '#3a414a'} fontWeight={item.bold ? 700 : 400}
        >
          T
        </text>
      )}
    </svg>
  );
}

export default function ShapeLibrary({ onPlace, onDragStart }) {
  const [query, setQuery] = useState('');
  const groups = useMemo(() => searchShapes(query), [query]);

  return (
    <div className="shlib">
      <div className="shlib-search">
        <input
          type="search"
          value={query}
          placeholder="Search shapes"
          aria-label="Search shapes"
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="shlib-scroll">
        {groups.length === 0 && (
          <p className="shlib-empty">Nothing matches “{query}”.</p>
        )}
        {groups.map((group) => (
          <section key={group.group}>
            <h4>{group.group}</h4>
            <div className="shlib-grid">
              {group.items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="shlib-item"
                  title={item.hint || item.label}
                  // Native HTML drag, not pointer events: the drop lands
                  // on the canvas, which is a different element, and a
                  // pointer capture on this button would swallow it.
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'copy';
                    e.dataTransfer.setData('text/plain', item.key);
                    onDragStart?.(item);
                  }}
                  onClick={() => onPlace(item)}
                >
                  <Preview item={item} />
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>

      <p className="shlib-hint">Drag onto the canvas, or click to drop one in the middle.</p>
    </div>
  );
}

export { SHAPE_LIBRARY };
